import React, { useEffect } from 'react';
import './SpacialRendAnimation.css';

const SpacialRendAnimation = ({ playerRef, opponentRef, onComplete }) => {
  useEffect(() => {
    const elements = [];

    if (!playerRef.current || !opponentRef.current) {
      console.warn('SpacialRendAnimation: Missing refs');
      onComplete();
      return;
    }

    const playerElement = playerRef.current;
    const opponentElement = opponentRef.current;

    // Get battle container for proper positioning
    const battleContainer = playerRef.current?.closest('.battle-screen') || 
                           playerRef.current?.closest('.battle-container') || 
                           document.body;

    // Get battle container rect for proper positioning
    const battleRect = battleContainer.getBoundingClientRect();
    const playerRect = playerElement.getBoundingClientRect();
    const opponentRect = opponentElement.getBoundingClientRect();
    
    // Calculate relative positions within battle container
    const playerX = playerRect.left - battleRect.left;
    const playerY = playerRect.top - battleRect.top;
    const opponentX = opponentRect.left - battleRect.left;
    const opponentY = opponentRect.top - battleRect.top;

    // Phase 1: Reality Distortion (0-700ms) - UNIQUE DIMENSIONAL MECHANIC
    playerElement.classList.add('spacial-rend-reality-distortion');

    // Create reality warping effect around entire battle area
    const realityWarp = document.createElement('div');
    realityWarp.className = 'spacial-rend-reality-warp';
    realityWarp.style.position = 'fixed';
    realityWarp.style.left = '0';
    realityWarp.style.top = '0';
    realityWarp.style.width = '100vw';
    realityWarp.style.height = '100vh';
    realityWarp.style.backgroundImage = `url('./Moves/PRAS- Spacial Rend BG.png')`;
    realityWarp.style.backgroundSize = 'cover';
    realityWarp.style.backgroundPosition = 'center';
    realityWarp.style.zIndex = '999';
    realityWarp.style.opacity = '0';
    battleContainer.appendChild(realityWarp);
    elements.push(realityWarp);

    // Create space distortion ripples emanating from attacker
    for (let i = 0; i < 5; i++) {
      const spaceRipple = document.createElement('div');
      spaceRipple.className = 'spacial-rend-space-ripple';
      spaceRipple.style.position = 'absolute';
      spaceRipple.style.left = `${playerX - 40 - (i * 20)}px`;
      spaceRipple.style.top = `${playerY - 40 - (i * 20)}px`;
      spaceRipple.style.width = `${80 + (i * 40)}px`;
      spaceRipple.style.height = `${80 + (i * 40)}px`;
      spaceRipple.style.animationDelay = `${i * 120}ms`;
      spaceRipple.style.zIndex = '1000';
      battleContainer.appendChild(spaceRipple);
      elements.push(spaceRipple);
    }

    // Create dimensional energy gathering at attacker
    const dimensionalCore = document.createElement('div');
    dimensionalCore.className = 'spacial-rend-dimensional-core';
    dimensionalCore.style.position = 'absolute';
    dimensionalCore.style.left = `${playerX - 25}px`;
    dimensionalCore.style.top = `${playerY - 25}px`;
    dimensionalCore.style.width = '50px';
    dimensionalCore.style.height = '50px';
    dimensionalCore.style.zIndex = '1001';
    battleContainer.appendChild(dimensionalCore);
    elements.push(dimensionalCore);

    // Phase 2: Spatial Tear Creation (700-1300ms) - UNIQUE TEARING MECHANIC  
    const phase2Timer = setTimeout(() => {
      // Intensify reality warp
      realityWarp.style.opacity = '0.8';

      // Create dimensional tears across the battlefield (unique mechanic)
      for (let i = 0; i < 4; i++) {
        const dimensionalTear = document.createElement('div');
        dimensionalTear.className = 'spacial-rend-dimensional-tear';
        dimensionalTear.style.position = 'absolute';
        dimensionalTear.style.left = `${playerX + (i * 60) - 100}px`;
        dimensionalTear.style.top = `${playerY + (i * 20) - 40}px`;
        dimensionalTear.style.width = '8px';
        dimensionalTear.style.height = `${80 + (i * 20)}px`;
        dimensionalTear.style.transform = `rotate(${15 + (i * 10)}deg)`;
        dimensionalTear.style.animationDelay = `${i * 100}ms`;
        dimensionalTear.style.zIndex = '1002';
        battleContainer.appendChild(dimensionalTear);
        elements.push(dimensionalTear);
      }

      // Create void fragments leaking from tears
      for (let i = 0; i < 8; i++) {
        const voidFragment = document.createElement('div');
        voidFragment.className = 'spacial-rend-void-fragment';
        voidFragment.textContent = '◆';
        voidFragment.style.position = 'absolute';
        voidFragment.style.left = `${playerX + Math.cos(i * 45 * Math.PI / 180) * 80}px`;
        voidFragment.style.top = `${playerY + Math.sin(i * 45 * Math.PI / 180) * 80}px`;
        voidFragment.style.animationDelay = `${i * 75}ms`;
        voidFragment.style.zIndex = '1003';
        battleContainer.appendChild(voidFragment);
        elements.push(voidFragment);
      }

      // Row 2 sprites (118x24, 118x26, 118x32)
      const row2Sprites = [
        { x: 0, y: 120, width: 118, height: 24 },
        { x: 118, y: 120, width: 118, height: 26 },
        { x: 236, y: 120, width: 118, height: 32 }
      ];

      row2Sprites.forEach((sprite, index) => {
        const rendSprite = document.createElement('div');
        rendSprite.className = 'spacial-rend-sprite-row2';
        rendSprite.style.position = 'absolute';
        rendSprite.style.left = `${opponentX + (index * 40) - 50}px`;
        rendSprite.style.top = `${opponentY + 30}px`;
        rendSprite.style.width = `${sprite.width}px`;
        rendSprite.style.height = `${sprite.height}px`;
        rendSprite.style.backgroundImage = `url('./Moves/PRAS- Spacial Rend.png')`;
        rendSprite.style.backgroundPosition = `-${sprite.x}px -${sprite.y}px`;
        rendSprite.style.backgroundSize = '960px 768px';
        rendSprite.style.animationDelay = `${index * 100}ms`;
        rendSprite.style.zIndex = '1003';
        battleContainer.appendChild(rendSprite);
        elements.push(rendSprite);
      });

      // Create dimensional rift effects
      for (let i = 0; i < 6; i++) {
        const riftEffect = document.createElement('div');
        riftEffect.className = 'spacial-rend-rift-effect';
        riftEffect.textContent = '⚡';
        riftEffect.style.position = 'absolute';
        riftEffect.style.left = `${opponentX + (i * 30) - 90}px`;
        riftEffect.style.top = `${opponentY + (i * 15) - 45}px`;
        riftEffect.style.transform = `rotate(${i * 30}deg)`;
        riftEffect.style.animationDelay = `${i * 60}ms`;
        riftEffect.style.zIndex = '1004';
        battleContainer.appendChild(riftEffect);
        elements.push(riftEffect);
      }
    }, 600);
    elements.push({ type: 'timer', timer: phase2Timer });

    // Phase 3: Spacial rend impact (1200-2000ms)
    const phase3Timer = setTimeout(() => {
      opponentElement.classList.add('spacial-rend-opponent-hit');

      // Create massive dimensional explosion
      const dimensionalExplosion = document.createElement('div');
      dimensionalExplosion.className = 'spacial-rend-dimensional-explosion';
      dimensionalExplosion.style.position = 'absolute';
      dimensionalExplosion.style.left = `${opponentX - 150}px`;
      dimensionalExplosion.style.top = `${opponentY - 150}px`;
      dimensionalExplosion.style.width = '300px';
      dimensionalExplosion.style.height = '300px';
      dimensionalExplosion.style.zIndex = '1005';
      battleContainer.appendChild(dimensionalExplosion);
      elements.push(dimensionalExplosion);

      // Create Row 3 sprites for explosion effect (120x120, 120x24, 120x26, 120x32)
      const row3Sprites = [
        { x: 0, y: 240, width: 120, height: 120 },
        { x: 120, y: 240, width: 120, height: 24 },
        { x: 240, y: 240, width: 120, height: 26 },
        { x: 360, y: 240, width: 120, height: 32 }
      ];

      row3Sprites.forEach((sprite, index) => {
        const explosionSprite = document.createElement('div');
        explosionSprite.className = 'spacial-rend-explosion-sprite';
        explosionSprite.style.position = 'absolute';
        explosionSprite.style.left = `${opponentX + Math.cos(index * 90 * Math.PI / 180) * 80}px`;
        explosionSprite.style.top = `${opponentY + Math.sin(index * 90 * Math.PI / 180) * 80}px`;
        explosionSprite.style.width = `${sprite.width}px`;
        explosionSprite.style.height = `${sprite.height}px`;
        explosionSprite.style.backgroundImage = `url('./Moves/PRAS- Spacial Rend.png')`;
        explosionSprite.style.backgroundPosition = `-${sprite.x}px -${sprite.y}px`;
        explosionSprite.style.backgroundSize = '960px 768px';
        explosionSprite.style.animationDelay = `${index * 50}ms`;
        explosionSprite.style.zIndex = '1006';
        battleContainer.appendChild(explosionSprite);
        elements.push(explosionSprite);
      });

      // Create Row 4 sprites for final effects
      const row4Sprites = [
        { x: 0, y: 360, width: 118, height: 120 },
        { x: 118, y: 360, width: 118, height: 24 },
        { x: 236, y: 360, width: 118, height: 26 },
        { x: 354, y: 360, width: 32, height: 120 },
        { x: 386, y: 360, width: 32, height: 24 },
        { x: 418, y: 360, width: 32, height: 26 }
      ];

      row4Sprites.forEach((sprite, index) => {
        const finalSprite = document.createElement('div');
        finalSprite.className = 'spacial-rend-final-sprite';
        finalSprite.style.position = 'absolute';
        finalSprite.style.left = `${opponentX + Math.cos(index * 60 * Math.PI / 180) * 100}px`;
        finalSprite.style.top = `${opponentY + Math.sin(index * 60 * Math.PI / 180) * 100}px`;
        finalSprite.style.width = `${sprite.width}px`;
        finalSprite.style.height = `${sprite.height}px`;
        finalSprite.style.backgroundImage = `url('./Moves/PRAS- Spacial Rend.png')`;
        finalSprite.style.backgroundPosition = `-${sprite.x}px -${sprite.y}px`;
        finalSprite.style.backgroundSize = '960px 768px';
        finalSprite.style.animationDelay = `${index * 40}ms`;
        finalSprite.style.zIndex = '1007';
        battleContainer.appendChild(finalSprite);
        elements.push(finalSprite);
      });

      // Enhanced explosion for potential critical hit (high crit ratio move)
      if (Math.random() < 0.4) { // 40% chance for enhanced explosion
        dimensionalExplosion.classList.add('spacial-rend-crit-explosion');
      }

      // Create dimensional shockwave rings
      for (let i = 0; i < 5; i++) {
        const shockwave = document.createElement('div');
        shockwave.className = 'spacial-rend-shockwave';
        shockwave.style.position = 'absolute';
        shockwave.style.left = `${opponentX - 80 - (i * 30)}px`;
        shockwave.style.top = `${opponentY - 80 - (i * 30)}px`;
        shockwave.style.width = `${160 + (i * 60)}px`;
        shockwave.style.height = `${160 + (i * 60)}px`;
        shockwave.style.animationDelay = `${i * 100}ms`;
        shockwave.style.zIndex = '1004';
        battleContainer.appendChild(shockwave);
        elements.push(shockwave);
      }

      // Create dragon energy burst
      for (let i = 0; i < 24; i++) {
        const energyBurst = document.createElement('div');
        energyBurst.className = 'spacial-rend-energy-burst';
        energyBurst.textContent = '🐲';
        energyBurst.style.position = 'absolute';
        energyBurst.style.left = `${opponentX + Math.cos(i * 15 * Math.PI / 180) * 90}px`;
        energyBurst.style.top = `${opponentY + Math.sin(i * 15 * Math.PI / 180) * 90}px`;
        energyBurst.style.animationDelay = `${i * 20}ms`;
        energyBurst.style.zIndex = '1005';
        battleContainer.appendChild(energyBurst);
        elements.push(energyBurst);
      }
    }, 1200);
    elements.push({ type: 'timer', timer: phase3Timer });

    // Phase 4: Reset and cleanup (2000-2500ms)
    const cleanupTimer = setTimeout(() => {
      // Remove player effects
      playerElement.classList.remove('spacial-rend-user-charge');
      
      // Remove opponent effects
      if (opponentRef.current) {
        opponentRef.current.classList.remove('spacial-rend-opponent-hit');
      }

      // Clean up all elements
      elements.forEach(element => {
        if (element.type === 'timer') {
          clearTimeout(element.timer);
        } else if (element && element.parentNode) {
          element.remove();
        }
      });

      onComplete();
    }, 2500);

    // Cleanup function
    return () => {
      playerElement.classList.remove('spacial-rend-user-charge');
      if (opponentRef.current) {
        opponentRef.current.classList.remove('spacial-rend-opponent-hit');
      }
      
      elements.forEach(element => {
        if (element.type === 'timer') {
          clearTimeout(element.timer);
        } else if (element && element.parentNode) {
          element.remove();
        }
      });
      
      clearTimeout(cleanupTimer);
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
};

export default SpacialRendAnimation;
